import { useEffect, useState } from "react";

import { SafeArea } from "capacitor-plugin-safe-area";
import { SplashScreen } from "@capacitor/splash-screen";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { Capacitor } from "@capacitor/core";
import axios from "axios";
import { Sentry } from "@shared/utils/init-tracking";

import { useAuth } from "@shared/features/auth/hooks/useAuth";
import { useApi } from "@shared/features/api/hooks/useApi";

import { version as packageVersion } from "../../../../package.json";

export const useApp = () => {
  const auth = useAuth();
  const api = useApi();
  const [initialized, setInitialized] = useState(false);

  const handleSafeArea = (data) => {
    for (const [key, value] of Object.entries(data.insets)) {
      document.documentElement.style.setProperty(
        `--safe-area-${key}`,
        `${value}px`
      );
    }
  };

  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();

    SafeArea.getSafeAreaInsets().then(handleSafeArea);
    SafeArea.addListener("safeAreaChanged", handleSafeArea);
    auth.init();
    auth.ensureUserIsLoggedIn();

    if (isNative) {
      SafeArea.setImmersiveNavigationBar();

      (async () => {
        CapacitorUpdater.notifyAppReady();
        SplashScreen.show();

        try {
          const [current, latest, { bundles }] = await Promise.all([
            CapacitorUpdater.current(),
            api.ota.get("latest"),
            CapacitorUpdater.list(),
          ]);

          const currentVersion =
            current.bundle.version === "builtin"
              ? current.native
              : current.bundle.version;

          if (
            currentVersion.localeCompare(latest.version, undefined, {
              numeric: true,
            }) >= 0
          ) {
            setInitialized(true);
            SplashScreen.hide();

            return;
          }

          // Check if the the bundle is already downloaded
          let bundle = bundles.find((x) => x.version === latest.version);

          // Otherwise download it
          if (!bundle) {
            latest.url = new URL(latest.url, api.baseUrl).toString();
            bundle = await CapacitorUpdater.download(latest);
          }

          // Use the downloaded bundle
          await CapacitorUpdater.set(bundle);
        } catch {
          setInitialized(true);
          SplashScreen.hide();
        }
      })();
    } else {
      (async () => {
        let cacheBustingFor = localStorage.getItem("cacheBustingFor");

        if (
          cacheBustingFor === packageVersion ||
          packageVersion.localeCompare(cacheBustingFor, undefined, {
            numeric: true,
          }) >= 0
        ) {
          cacheBustingFor = undefined;
          localStorage.removeItem("cacheBustingFor");
          console.log("Cache busting successful");
          Sentry.addBreadcrumb({
            category: "app",
            message: "Cache busted",
            data: { currentVersion: packageVersion },
          });
        }

        const {
          data: { version: latestVersion },
        } = await axios.get(`/meta.json?${Date.now()}`, {
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Pragma: "no-cache",
            Expires: "0",
          },
        });

        console.log("App Version", {
          latestVersion,
          currentVersion: packageVersion,
          updatingTo: cacheBustingFor,
        });

        if (
          !latestVersion ||
          packageVersion.localeCompare(latestVersion, undefined, {
            numeric: true,
          }) >= 0
        ) {
          setInitialized(true);

          return;
        }

        if (cacheBustingFor === latestVersion) {
          console.warn(`Cache busting failed, staying on v${packageVersion}`);
          setInitialized(true);
          Sentry.captureMessage("Cache busting failed", {
            level: "warning",
            extra: {
              latestVersion,
              currentVersion: packageVersion,
            },
          });

          return;
        }

        localStorage.setItem("cacheBustingFor", latestVersion);

        if (window?.caches) {
          const { caches } = window;
          const cacheNames = await caches.keys();
          const cacheDeletionPromises = cacheNames.map((n) => caches.delete(n));

          await Promise.all(cacheDeletionPromises);
        }

        Sentry.addBreadcrumb({
          category: "app",
          message: "Cache busting in progress",
          data: {
            latestVersion,
            currentVersion: packageVersion,
          },
        });

        const cacheBustedUrl = new URL(window.location.toString());

        cacheBustedUrl.searchParams.set("version", String(Date.now()));
        window.location.replace(cacheBustedUrl);
      })();
    }

    return () => {
      SafeArea.removeAllListeners();
    };
  }, []);

  return { initialized };
};
